import { useMemo, useRef } from "react";
import {
  Box,
  Flex,
  NotFoundPage,
  PaginationProvider,
  SortProvider,
  Spinner,
  NameSection,
  PropertyPreviewNavigationTabs,
  FeaturesAndAmenitiesList,
  AdditionalInfoSection,
  MapSection,
  DevelopInfoSection,
  InventorySectionProvider,
  DropdownOptions,
  TooltipPopup,
} from "dash-ui";
import { useNavigate, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import {
  GetPropertyByIdQuery,
  useGetAmenitiesQuery,
  useGetPropertyByIdQuery,
} from "gql/generated.graphql";
import Header from "flow-components/Header/Header";
import Container from "flow-components/Container/Container";
import { RoutesPath } from "flow-components/routes/routes";
import SlideShowSection from "pages/properties/PropertyPreviewPage/components/SlideShowSection/SlideShowSection";

import InventoryTableSection from "./components/InventoryTableSection/InventoryTableSection";
import MarketingFilesSection from "./components/MarketingFilesSection/MarketingFilesSection";
import { useBrowserTabTitle } from "common/hooks/use-browser-tab-title/use-browser-tab-title";
import { useTranslation } from "react-i18next";
import { GeneralInfoSection as PropertyGeneralInfoSection } from "v2/components/property-general-info-section";

const contentStyle = css`
  background-color: transparent;
  border-radius: unset;
  box-shadow: unset;
`;

const ContainerWrapper = styled(Flex)`
  max-width: ${({ theme }) => theme.breakpoints.xl + 88}px;
  align-self: center;
  width: 100%;
  flex-grow: 0;

  & > div > div:first-child {
    padding-left: ${({ theme }) => theme.space * 37.5}px;
    padding-right: ${({ theme }) => theme.space * 37.5}px;
    @media ${({ theme }) => theme.devices.mobile} {
      padding-left: ${({ theme }) => theme.space * 4}px;
      padding-right: ${({ theme }) => theme.space * 4}px;
    }
    @media ${({ theme }) => theme.devices.tablet} {
      padding-left: ${({ theme }) => theme.space * 14}px;
      padding-right: ${({ theme }) => theme.space * 14}px;
    }
  }
`;
const BackgroundStyle = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.n20};
  width: 100%;
  flex-grow: 0;
`;

function PropertyPreviewPageContent({
  property,
}: {
  property: GetPropertyByIdQuery["getPropertyById"];
}) {
  const { t } = useTranslation();
  const generalInfoRef = useRef(null);
  const additionalInfoRef = useRef(null);
  const marketingFilesInfoRef = useRef(null);

  useBrowserTabTitle(`${property.name} ${t("common:labels.dashSuffix")}`);

  const { data, loading } = useGetAmenitiesQuery({
    variables: {
      filter: { propertyId: property._id },
      pagination: { allowLimit: false },
    },
  });

  const amenities = useMemo(() => data?.getAmenities.collection || [], [data]);

  return (
    <>
      <DropdownOptions />
      <TooltipPopup />
      <Header />
      <BackgroundStyle justifyContent="center">
        <ContainerWrapper>
          <Container
            withSidebar={false}
            stickyBreadcrumbs
            contentStyle={contentStyle}
          >
            <Flex flexDirection="column" flex={1} w="100%">
              <SlideShowSection property={property} />
              <NameSection property={property} />
              <PropertyPreviewNavigationTabs
                navigationElementsRefs={[
                  generalInfoRef,
                  additionalInfoRef,
                  marketingFilesInfoRef,
                ]}
              />
              <Box w="100%">
                <PropertyGeneralInfoSection
                  data={{
                    highlights: property.projectDetails,
                    overview: property.overview,
                    brokerCommission: property.brokerCommission,
                  }}
                />
                <FeaturesAndAmenitiesList
                  amenities={amenities}
                  loading={loading}
                />
                <PaginationProvider perPage={5}>
                  <SortProvider>
                    <InventorySectionProvider>
                      <InventoryTableSection filter={{ property }} />
                    </InventorySectionProvider>
                  </SortProvider>
                </PaginationProvider>
                <AdditionalInfoSection
                  containerRef={additionalInfoRef}
                  property={property}
                />
                <MapSection property={property} />
                <DevelopInfoSection property={property} />
                <MarketingFilesSection
                  property={property}
                  containerRef={marketingFilesInfoRef}
                />
              </Box>
            </Flex>
          </Container>
        </ContainerWrapper>
      </BackgroundStyle>
    </>
  );
}

/**
 * @deprecated This page is being replaced by PropertyUserPreviewPage.
 * Please use PropertyUserPreviewPage for new features.
 * TODO: Remove after testing is complete and all references are migrated.
 * - Migration checklist:
 * [ ] Test agent preview functionality
 * [ ] Test direct user preview functionality
 * [ ] Update all links to use new routes
 * [ ] Remove references in navigation/menus
 * [ ] Delete this file and components folder
 */
function PropertyPreviewPage() {
  const { id } = useParams();
  const { data, loading } = useGetPropertyByIdQuery({
    variables: { propertyId: id },
  });
  const property = data?.getPropertyById;
  const navigate = useNavigate();

  if (!property && !loading) {
    return <NotFoundPage onRedirect={() => navigate(RoutesPath.Properties)} />;
  }

  if (loading) {
    return <Spinner size={42} fullContent />;
  }

  return <PropertyPreviewPageContent property={property!} />;
}

export default PropertyPreviewPage;
